import React, { useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import authorizationAgreementApi, { Agreement, AgreementSignability } from '../../../utils/api/authorizationAgreement';

interface Props {
  projectId: number;
  agreement: Agreement | null;
  signability: AgreementSignability;
  onSignNow: () => void;
  onDownload: () => void;
  onAgreementUpdated: () => void;
}

const AuthorizationAgreementBanner: React.FC<Props> = ({
  projectId,
  agreement,
  signability,
  onSignNow,
  onDownload,
  onAgreementUpdated,
}) => {
  const [busy, setBusy] = useState(false);

  const handleWaive = async () => {
    setBusy(true);
    try {
      await authorizationAgreementApi.waiveRequirement(projectId);
      onAgreementUpdated();
      toast.success('Authorization & Agreement marked as not required for this project');
    } catch (err) {
      const error = err as any;
      toast.error(error?.response?.data?.error || 'Error updating requirement');
    } finally {
      setBusy(false);
    }
  };

  const handleUnwaive = async () => {
    setBusy(true);
    try {
      await authorizationAgreementApi.unwaiveRequirement(projectId);
      onAgreementUpdated();
      toast.success('Authorization & Agreement requirement restored');
    } catch (err) {
      const error = err as any;
      toast.error(error?.response?.data?.error || 'Error updating requirement');
    } finally {
      setBusy(false);
    }
  };

  // Waived state
  if (agreement?.waived_at) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f5f5f5',
          border: '1px solid #ddd',
          borderLeft: '3px solid #999',
          borderRadius: '4px',
          padding: '9px 14px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#555', fontWeight: 500 }}>
          Authorization &amp; Agreement
          <span style={{ background: '#888', color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>
            Not Required
          </span>
          <span style={{ fontSize: '11px', color: '#777', fontWeight: 400 }}>
            · Marked not required {moment(agreement.waived_at).format('MMM D, YYYY')}
          </span>
        </div>
        {signability.canWaive && (
          <button
            onClick={handleUnwaive}
            disabled={busy}
            style={{ background: 'none', border: '1px solid #999', color: '#555', padding: '5px 12px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Undo
          </button>
        )}
      </div>
    );
  }

  // Executed state
  if (agreement?.status === 'executed') {
    const clientAt = agreement.client_signed_at ? new Date(agreement.client_signed_at) : null;
    const inspectorAt = agreement.inspector_signed_at ? new Date(agreement.inspector_signed_at) : null;
    const signedAt =
      clientAt && inspectorAt
        ? clientAt > inspectorAt ? clientAt : inspectorAt
        : clientAt ?? inspectorAt;
    const signedLabel = signedAt ? moment(signedAt).format('MMM D, YYYY') : null;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f0faf6',
          borderLeft: '3px solid #1D9E75',
          border: '1px solid #b8e8d4',
          borderRadius: '4px',
          padding: '9px 14px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#085041', fontWeight: 500 }}>
          Authorization &amp; Agreement
          <span style={{ background: '#1D9E75', color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>
            Executed
          </span>
          {signedLabel && (
            <span style={{ fontSize: '11px', color: '#0F6E56', fontWeight: 400 }}>
              · Signed {signedLabel}
            </span>
          )}
        </div>
        <span
          onClick={onDownload}
          style={{ fontSize: '12px', color: '#1D9E75', textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          Download PDF
        </span>
      </div>
    );
  }

  // Pending / unsigned state
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#fffbf2',
        borderLeft: '3px solid #BA7517',
        border: '1px solid #fad68a',
        borderRadius: '4px',
        padding: '9px 14px',
        marginBottom: '16px',
      }}
    >
      <div>
        <div style={{ fontSize: '13px', color: '#633806', fontWeight: 500 }}>
          Authorization &amp; Agreement · Signature required
        </div>
        <div style={{ fontSize: '11px', color: '#854F0B', marginTop: '2px' }}>
          Inspections are locked until both parties have signed
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
        {signability.canWaive && (
          <button
            onClick={handleWaive}
            disabled={busy}
            style={{ background: 'none', border: '1px solid #BA7517', color: '#633806', padding: '5px 12px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Not required for this project
          </button>
        )}
        <button
          onClick={onSignNow}
          style={{ background: '#1D9E75', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}
        >
          Sign now
        </button>
      </div>
    </div>
  );
};

export default AuthorizationAgreementBanner;
